<template>
  <div
    ref="container"
    class="relative"
    @keydown.down="moveHighlight($event, 1)"
    @keydown.up="moveHighlight($event, -1)"
    @keydown.enter="selectHighlighted"
    @keydown.esc="onEscape"
  >
    <component
      :is="field"
      class="w-full"
      :model-value="model"
      :placeholder="placeholder"
      :errors="errors"
      :disabled="disabled"
      v-bind="fieldProps"
      @update:model-value="onInput"
      @focus="onFocus"
    />
    <div
      v-if="isDropdownVisible"
      :id="listboxId"
      ref="options"
      role="listbox"
      class="z-10 absolute bg-white shadow-elevation-large mt-1 py-2 rounded-md min-w-full max-md:w-full overflow-y-auto overscroll-none"
      :class="{'w-full': cropSuggestions}"
      :style="{ maxHeight: optionsMaxHeight }"
    >
      <template v-if="visibleSuggestions.length">
        <div
          v-for="(suggestion, index) in visibleSuggestions"
          :id="optionId(index)"
          :key="index"
          role="option"
          :aria-selected="index === highlightedIndex"
          class="px-4 py-2 text-sm truncate cursor-pointer hover:bg-grey-100"
          :class="{'bg-grey-100': index === highlightedIndex}"
          @mouseenter="highlightedIndex = index"
          @mousedown.prevent
          @click="selectSuggestion(suggestion)"
        >
          <slot name="suggestion" :suggestion="suggestion" :query="markedPhrase">
            <div class="flex items-center gap-2">
              <span class="flex-1 truncate" v-html="highlightPhrase(markedPhrase, suggestionLabel(suggestion))" />
              <slot name="suffix" :suggestion="suggestion" />
            </div>
          </slot>
        </div>
      </template>
      <div v-else-if="loading" class="px-4 py-2 text-grey-600 text-sm">
        Searching…
      </div>
      <div v-else class="px-4 py-3 text-grey-600 text-sm">
        {{ noResultsText }}
      </div>
      <slot name="footer" />
    </div>
  </div>
</template>

<script setup lang="ts" generic="C extends Component = typeof TextField">
import { onClickOutside, useFocusWithin } from '@vueuse/core';
import type { Component } from 'vue';
import { computed, ref, useId, useTemplateRef, watch, watchPostEffect } from 'vue';

import TextField from '@/components/Ui/Forms/TextField.vue';
import highlightPhrase from '@/utils/highlight-phrase';

export interface FieldSuggestion {
  value: string | number;
  label: string;
  description?: string;
}

type FieldProps<T> = T extends new (...args: never[]) => { $props: infer P }
  ? Partial<P> & Record<string, unknown>
  : Record<string, unknown>;

interface Props {
  suggestions: FieldSuggestion[];
  fieldComponent?: C;
  fieldProps?: FieldProps<C>;
  placeholder?: string;
  errors?: string[];
  disabled?: boolean;
  filterable?: boolean;
  minChars?: number;
  maxVisibleSuggestions?: number;
  cropSuggestions?: boolean;
  noResultsText?: string;
  /** Enter with nothing highlighted picks the first suggestion instead of submitting the form. */
  selectFirstOnEnter?: boolean;
  /**
   * Shows a "Searching…" row while async results are being fetched, but only when there is
   * nothing else to show - an already displayed list is never swapped for the loader.
   */
  loading?: boolean;
  /**
   * Phrase marked in the suggestion labels. Async lists lag a keystroke or two behind the
   * input, so marking them against the live query makes the highlight jitter while typing;
   * pass the query the suggestions were actually fetched for instead.
   */
  highlightQuery?: string | null;
  /**
   * Arrow keys write the highlighted suggestion into the visible input (like Google's legacy
   * autocomplete); Esc or closing the dropdown restores the typed text. The model only
   * changes on actual selection.
   */
  previewHighlight?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  fieldComponent: undefined,
  fieldProps: () => ({} as FieldProps<C>),
  placeholder: '',
  errors: () => [],
  disabled: false,
  filterable: true,
  minChars: 0,
  maxVisibleSuggestions: 7.5,
  cropSuggestions: false,
  noResultsText: '',
  selectFirstOnEnter: false,
  loading: false,
  highlightQuery: null,
  previewHighlight: false,
});

const emit = defineEmits<{
  select: [suggestion: FieldSuggestion];
  focus: [];
  blur: [];
  openChanged: [isOpen: boolean];
}>();

const model = defineModel<string | number>({ default: '' });

const containerRef = useTemplateRef<HTMLElement>('container');
const optionsRef = useTemplateRef<HTMLElement>('options');
const inputElement = computed(() => containerRef.value?.querySelector('input') ?? null);
const isOpen = ref(false);
const highlightedIndex = ref(-1);
const listboxId = useId();

const field = computed<Component>(() => props.fieldComponent ?? TextField);
const query = computed(() => String(model.value ?? ''));
const markedPhrase = computed(() => props.highlightQuery ?? query.value);

function suggestionLabel(suggestion: FieldSuggestion): string {
  return suggestion.description ? `${suggestion.label} • ${suggestion.description}` : suggestion.label;
}

const visibleSuggestions = computed<FieldSuggestion[]>(() => {
  if (!props.filterable) {
    return props.suggestions;
  }
  const phrase = query.value.trim().toLowerCase();
  if (!phrase) {
    return props.suggestions;
  }
  return props.suggestions.filter(suggestion => suggestionLabel(suggestion).toLowerCase().includes(phrase));
});

const suggestionRowHeightPx = 36;
const listVerticalPaddingPx = 16;
const optionsMaxHeight = computed(
  () => `${listVerticalPaddingPx + props.maxVisibleSuggestions * suggestionRowHeightPx}px`,
);

const isDropdownVisible = computed(() => isOpen.value
  && query.value.trim().length >= props.minChars
  && (props.loading || visibleSuggestions.value.length > 0 || props.noResultsText !== ''));

onClickOutside(containerRef, () => {
  isOpen.value = false;
});

const { focused } = useFocusWithin(containerRef);
watch(focused, (isFocused) => {
  if (!isFocused) {
    isOpen.value = false;
    emit('blur');
  }
});

// Writes straight to the DOM input on purpose: going through the model would count as
// typing (refetching async suggestions and re-marking the list) while the user is only
// browsing. Vue leaves the DOM value alone as long as the model doesn't change.
function previewHighlightedSuggestion(): void {
  const suggestion = visibleSuggestions.value[highlightedIndex.value];
  if (suggestion && inputElement.value) {
    inputElement.value.value = String(suggestion.value);
  }
}

function restoreInputFromModel(): void {
  if (inputElement.value) {
    inputElement.value.value = String(model.value ?? '');
  }
}

watch([isOpen, visibleSuggestions], () => {
  highlightedIndex.value = -1;
});

// Esc, click-outside and selection all land here; after a selection the model already
// holds the picked value, so the restore is a no-op.
watch(isOpen, (open) => {
  if (!open && props.previewHighlight) {
    restoreInputFromModel();
  }
});

watch(isDropdownVisible, value => emit('openChanged', value));

function optionId(index: number): string {
  return `${listboxId}-${index}`;
}

// Keeps combobox ARIA attributes in sync on the field's inner <input>,
// which is out of template reach (BaseInputField doesn't forward attrs to it).
watchPostEffect(() => {
  const input = inputElement.value;
  if (!input) {
    return;
  }
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-controls', listboxId);
  input.setAttribute('aria-expanded', String(isDropdownVisible.value));
  if (isDropdownVisible.value && highlightedIndex.value >= 0) {
    input.setAttribute('aria-activedescendant', optionId(highlightedIndex.value));
  } else {
    input.removeAttribute('aria-activedescendant');
  }
});

function onFocus(): void {
  isOpen.value = true;
  emit('focus');
}

function onInput(value: string | number): void {
  model.value = value;
  isOpen.value = true;
}

function selectSuggestion(suggestion: FieldSuggestion): void {
  model.value = suggestion.value;
  emit('select', suggestion);
  isOpen.value = false;
}

function moveHighlight(event: KeyboardEvent, step: number): void {
  isOpen.value = true;
  const count = visibleSuggestions.value.length;
  if (!isDropdownVisible.value || !count) {
    return;
  }
  event.preventDefault();
  highlightedIndex.value = (highlightedIndex.value + step + count) % count;
  optionsRef.value?.children[highlightedIndex.value]?.scrollIntoView({ block: 'nearest' });
  if (props.previewHighlight) {
    previewHighlightedSuggestion();
  }
}

function onEscape(): void {
  isOpen.value = false;
}

function selectHighlighted(event: KeyboardEvent): void {
  if (!isDropdownVisible.value) {
    return;
  }
  const fallbackIndex = props.selectFirstOnEnter && visibleSuggestions.value.length > 0 ? 0 : -1;
  const index = highlightedIndex.value >= 0 ? highlightedIndex.value : fallbackIndex;
  if (index < 0) {
    return;
  }

  event.preventDefault();
  selectSuggestion(visibleSuggestions.value[index]);
}
</script>
